/*
  Shared rendering primitives for PhenoScan sub-tab UIs.
  Keeps per-module files tiny (~150 lines) while delivering consistent UX.
*/
import React, { useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import ValidationPanel from '@/components/ValidationPanel';
import {
  Upload, Image as ImageIcon, Download, Play, Check, AlertTriangle, Copy, Flame,
} from 'lucide-react';

/** Generic sub-tab scaffold used by Seed / Disease / Panicle / Leaf. */
export default function PhenoScanSubTab({
  testIdPrefix,          // e.g. 'seed'
  analyzeEndpoint,       // '/phenoscan/seed/analyze'
  sampleEndpoint,        // '/phenoscan/seed/sample'
  sampleMetaEndpoint,    // '/phenoscan/seed/sample-meta'
  exportKey,             // 'seed' | 'disease' | 'panicle' | 'leaf'
  title,                 // 'Seed & Fruit'
  extraParams = {},      // {<paramName>: defaultValue} sent as Form() fields
  renderResult,          // (data) => JSX for the module-specific results
  traitExtractor,        // (data) => { k: v } trait matrix for hand-offs
  gtFormField,           // name of the ground-truth Form() field
  calibrationRequired = true,
  heatmapKind,           // optional: 'leaf_greenness' | 'disease_lesion' | ...
  heatmapColormap = 'viridis',
}) {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [params, setParams] = useState({ aruco_marker_cm: 3.0, coin_diameter_cm: 2.3, ...extraParams });
  const [groundTruth, setGroundTruth] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [overlay, setOverlay] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(null);

  const onPickFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setImageFile(f); setImagePreview(URL.createObjectURL(f));
    setResult(null); setOverlay(null); setElapsedMs(null); setGroundTruth('');
  };

  const loadSample = async () => {
    setBusy(true);
    try {
      const [imgR, metaR] = await Promise.all([
        axios.get(`${API}${sampleEndpoint}`, { responseType: 'blob' }),
        axios.get(`${API}${sampleMetaEndpoint}`),
      ]);
      const f = new File([imgR.data], metaR.data.filename || 'sample.png', { type: 'image/png' });
      setImageFile(f);
      setImagePreview(URL.createObjectURL(imgR.data));
      setGroundTruth(String(metaR.data[gtFormField] ?? ''));
      setResult(null); setOverlay(null); setElapsedMs(null);
      toast.success(`Sample loaded — GT ${gtFormField.replace(/^ground_truth_/, '')} = ${metaR.data[gtFormField]}`);
    } catch {
      toast.error('Failed to load sample');
    } finally { setBusy(false); }
  };

  const runAnalysis = async () => {
    if (!imageFile) return toast.error('Pick an image first');
    setBusy(true); setResult(null); setOverlay(null);
    const t0 = performance.now();
    try {
      const fd = new FormData(); fd.append('file', imageFile);
      for (const [k, v] of Object.entries(params)) fd.append(k, String(v));
      if (groundTruth) fd.append(gtFormField, String(groundTruth));
      const r = await axios.post(`${API}${analyzeEndpoint}`, fd);
      const data = r.data.results;
      setResult(data);
      setOverlay(data.overlay_png_base64 ? `data:image/png;base64,${data.overlay_png_base64}` : null);
      setElapsedMs(Math.round(performance.now() - t0));
      toast.success('Analysis complete');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally { setBusy(false); }
  };

  const exportAs = async (fmt) => {
    if (!result) return;
    try {
      const r = await axios.post(`${API}/phenoscan/export?fmt=${fmt}`,
        { analysis: exportKey, results: result }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `phenoscan_${exportKey}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const copySettings = () => {
    navigator.clipboard?.writeText(JSON.stringify({
      module: `phenoscan_${exportKey}`, params, version: 'bkagrid-phenoscan-1.1',
    }, null, 2));
    toast.success('Settings copied');
  };

  const traitMatrix = useMemo(() => traitExtractor ? traitExtractor(result) : null, [result, traitExtractor]);

  const handoff = useCallback((path, target) => {
    if (!traitMatrix) return toast.error('Run an analysis first');
    navigate(path, { state: { phenoscanHandoff: { ...traitMatrix, target } } });
    toast.success('Trait matrix handed off');
  }, [navigate, traitMatrix]);

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6" data-testid={`${testIdPrefix}-pane`}>
      {/* LEFT PANEL */}
      <div className="rounded-2xl p-5 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        <h3 className="font-display text-lg mb-3" style={{ color: '#1B4332' }}>1. Image</h3>

        <div className="rounded-xl p-4 mb-3 text-center cursor-pointer"
          style={{ border: '1px dashed #BCC5B5', background: '#FAFBF9' }}
          onClick={() => fileRef.current?.click()}
          data-testid={`${testIdPrefix}-upload-zone`}>
          {imagePreview ? (
            <img src={imagePreview} alt="Upload" style={{ maxHeight: 180, margin: '0 auto', borderRadius: 8 }} />
          ) : (
            <div className="py-6 text-sm" style={{ color: '#6F695F' }}>
              <Upload size={22} className="mx-auto mb-2" /> Click or drag an image
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile}
            data-testid={`${testIdPrefix}-upload-input`} />
        </div>

        <button onClick={loadSample} disabled={busy}
          className="w-full rounded-lg py-2 text-sm mb-5"
          style={{ background: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0' }}
          data-testid={`${testIdPrefix}-load-sample-btn`}>
          <ImageIcon size={14} className="inline mr-2" /> Load Sample (validated)
        </button>

        <h3 className="font-display text-lg mb-3" style={{ color: '#1B4332' }}>2. Parameters</h3>

        {calibrationRequired && (
          <div className="text-xs mb-3 rounded-md px-3 py-2"
            style={{ background: '#FFFBEB', color: '#78350F', border: '1px solid #FDE68A' }}>
            <AlertTriangle size={11} className="inline mr-1" />
            Calibration marker required for absolute metrics (cm / mm).
            Without one, output is in pixel units only.
          </div>
        )}

        <label className="block mb-3 text-sm">
          <div className="mb-1" style={{ color: '#4F4B44' }}>ArUco marker side (cm)</div>
          <input type="number" step="0.1" min="0.5" value={params.aruco_marker_cm}
            onChange={e => setParams(p => ({ ...p, aruco_marker_cm: parseFloat(e.target.value) || 0 }))}
            className="w-full rounded px-3 py-1.5"
            style={{ border: '1px solid #E5E5E0', background: '#fff' }}
            data-testid={`${testIdPrefix}-aruco-cm`} />
        </label>
        <label className="block mb-3 text-sm">
          <div className="mb-1" style={{ color: '#4F4B44' }}>Coin diameter fallback (cm)</div>
          <input type="number" step="0.1" min="0.5" value={params.coin_diameter_cm}
            onChange={e => setParams(p => ({ ...p, coin_diameter_cm: parseFloat(e.target.value) || 0 }))}
            className="w-full rounded px-3 py-1.5"
            style={{ border: '1px solid #E5E5E0', background: '#fff' }} />
        </label>

        {Object.keys(extraParams).map(k => (
          <label key={k} className="block mb-3 text-sm">
            <div className="mb-1" style={{ color: '#4F4B44' }}>{k.replace(/_/g, ' ')}</div>
            <input type={typeof extraParams[k] === 'boolean' ? 'checkbox' : 'number'}
              step="0.01"
              checked={typeof extraParams[k] === 'boolean' ? !!params[k] : undefined}
              value={typeof extraParams[k] === 'boolean' ? undefined : params[k] ?? ''}
              onChange={e => setParams(p => ({
                ...p,
                [k]: typeof extraParams[k] === 'boolean' ? e.target.checked : (e.target.value === '' ? '' : parseFloat(e.target.value)),
              }))}
              className="w-full rounded px-3 py-1.5"
              style={{ border: '1px solid #E5E5E0', background: '#fff' }}
              data-testid={`${testIdPrefix}-extra-${k}`} />
          </label>
        ))}

        <label className="block mb-4 text-sm">
          <div className="mb-1" style={{ color: '#4F4B44' }}>Ground truth (optional)</div>
          <input type="number" step="0.01" value={groundTruth}
            onChange={e => setGroundTruth(e.target.value)}
            placeholder={gtFormField}
            className="w-full rounded px-3 py-1.5"
            style={{ border: '1px solid #E5E5E0', background: '#fff' }}
            data-testid={`${testIdPrefix}-ground-truth`} />
        </label>

        <button onClick={runAnalysis} disabled={busy || !imageFile}
          className="w-full rounded-lg py-2.5 text-sm font-semibold disabled:opacity-50"
          style={{ background: '#1B4332', color: '#fff' }}
          data-testid={`${testIdPrefix}-run-btn`}>
          <Play size={14} className="inline mr-2" /> {busy ? 'Running…' : `Run ${title} Analysis`}
        </button>

        <button onClick={copySettings} disabled={busy}
          className="w-full rounded-lg py-2 text-xs mt-2"
          style={{ background: '#F3F6F3', color: '#4F4B44', border: '1px solid #E5E5E0' }}
          data-testid={`${testIdPrefix}-copy-settings`}>
          <Copy size={12} className="inline mr-2" /> Copy Settings (reproducibility)
        </button>
      </div>

      {/* RIGHT PANEL */}
      <div className="rounded-2xl p-5 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        {!result ? (
          <EmptyState title={title} />
        ) : (
          <div data-testid={`${testIdPrefix}-results`}>
            <div className="flex flex-wrap gap-2 items-center mb-4">
              <CalBadge cal={result.calibration} testid={`${testIdPrefix}-cal-badge`} />
              {result.ground_truth && <GTBadge gt={result.ground_truth} testid={`${testIdPrefix}-gt-badge`} />}
              {elapsedMs != null && (
                <span className="px-2.5 py-1 rounded-full text-xs"
                  style={{ background: '#F3F6F3', color: '#4F4B44' }}>⏱ {elapsedMs} ms</span>
              )}
              <div className="ml-auto flex gap-1">
                <ExportBtn onClick={() => exportAs('csv')} prefix={testIdPrefix}>CSV</ExportBtn>
                <ExportBtn onClick={() => exportAs('xlsx')} prefix={testIdPrefix}>XLSX</ExportBtn>
                <ExportBtn onClick={() => exportAs('pdf')} prefix={testIdPrefix}>PDF</ExportBtn>
                {heatmapKind && imageFile && (
                  <ViewHeatmapBtn file={imageFile} kind={heatmapKind}
                    colormap={heatmapColormap} navigate={navigate}
                    title={`${title} · ${imageFile.name}`}
                    pixelsPerCm={result?.calibration?.pixels_per_cm}
                    testid={`${testIdPrefix}-view-heatmap`} />
                )}
                {overlay && (
                  <a href={overlay} download={`${exportKey}_overlay.png`}
                    className="px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1"
                    style={{ background: '#F3F6F3', border: '1px solid #E5E5E0', color: '#4F4B44' }}
                    data-testid={`${testIdPrefix}-export-png`}>
                    <Download size={11} /> PNG
                  </a>
                )}
              </div>
            </div>

            {result.ground_truth && <GroundTruthBox gt={result.ground_truth} testid={`${testIdPrefix}-gt-box`} />}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <ImgPanel title="Original" src={imagePreview} />
              <ImgPanel title="Overlay" src={overlay} testid={`${testIdPrefix}-overlay-img`} />
            </div>

            {renderResult && renderResult(result)}

            <HandoffDock onHandoff={handoff} prefix={testIdPrefix} />
          </div>
        )}
      </div>
      <div className="lg:col-span-2">
        <ValidationPanel subTab={exportKey} subTabLabel={title} />
      </div>
    </div>
  );
}

// ── Shared subcomponents ───────────────────────────────────────────────
function EmptyState({ title }) {
  return (
    <div className="text-center py-16">
      <div className="font-display text-lg" style={{ color: '#6F695F' }}>No results yet</div>
      <div className="text-sm mt-1" style={{ color: '#8A8580' }}>
        Upload an image or click <b>Load Sample</b> to run a validated {title} analysis.
      </div>
    </div>
  );
}

function CalBadge({ cal, testid }) {
  if (!cal) return null;
  const ok = cal.calibrated;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs"
      style={{ background: ok ? '#ECFDF5' : '#FEF3C7', color: ok ? '#065F46' : '#92400E' }}>
      {ok ? <Check size={11} /> : <AlertTriangle size={11} />}
      {ok ? `Calibrated · ${cal.method} · ${cal.pixels_per_cm} px/cm` : 'UNCALIBRATED — relative units only'}
    </span>
  );
}

function GTBadge({ gt, testid }) {
  const ok = gt.status === 'OK';
  const m = gt.measured_cm2 ?? gt.measured_count ?? gt.measured_pct_lesion ?? gt.measured_length_cm;
  const e = gt.ground_truth_cm2 ?? gt.ground_truth_count ?? gt.ground_truth_pct_lesion ?? gt.ground_truth_length_cm;
  const dr = gt.diff_pct ?? gt.diff_abs_pct;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs"
      style={{ background: ok ? '#EFF6FF' : '#FEF3C7', color: ok ? '#1E40AF' : '#92400E' }}>
      Measured {m} vs GT {e} · Δ {dr}{gt.diff_abs_pct != null ? ' (abs)' : '%'}
    </span>
  );
}

function GroundTruthBox({ gt, testid }) {
  const ok = gt.status === 'OK';
  const m = gt.measured_cm2 ?? gt.measured_count ?? gt.measured_pct_lesion ?? gt.measured_length_cm;
  const e = gt.ground_truth_cm2 ?? gt.ground_truth_count ?? gt.ground_truth_pct_lesion ?? gt.ground_truth_length_cm;
  const dr = gt.diff_pct ?? gt.diff_abs_pct;
  return (
    <div data-testid={testid} className="rounded-xl p-3 mb-4 grid grid-cols-3 gap-3"
      style={{ background: ok ? '#F0FDF4' : '#FFFBEB',
        border: `1px solid ${ok ? '#BBF7D0' : '#FDE68A'}` }}>
      <div><div className="text-xs" style={{ color: '#6F695F' }}>BKAgriD measured</div>
        <div className="font-display text-xl" style={{ color: '#1B4332' }}>{m}</div></div>
      <div><div className="text-xs" style={{ color: '#6F695F' }}>Ground truth</div>
        <div className="font-display text-xl" style={{ color: '#1B4332' }}>{e}</div></div>
      <div><div className="text-xs" style={{ color: '#6F695F' }}>Difference</div>
        <div className="font-display text-xl" style={{ color: ok ? '#065F46' : '#92400E' }}>{dr} {ok ? '✓' : '⚠'}</div></div>
    </div>
  );
}

function ImgPanel({ title, src, testid }) {
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #E5E5E0' }}>
      <div className="text-xs px-3 py-1.5" style={{ background: '#F3F6F3', color: '#4F4B44' }}>{title}</div>
      <div className="bg-white flex items-center justify-center" style={{ height: 260 }}>
        {src ? <img data-testid={testid} src={src} alt={title} style={{ maxHeight: 260, maxWidth: '100%' }} /> :
          <div className="text-sm" style={{ color: '#BCC5B5' }}>—</div>}
      </div>
    </div>
  );
}

function HandoffDock({ onHandoff, prefix }) {
  const targets = [
    ['Path Analysis', '/biometrical-analysis', 'path_analysis'],
    ['PCA', '/biometrical-analysis', 'pca'],
    ['D² & Cluster', '/biometrical-analysis', 'd_squared'],
    ['Heritability', '/biometrical-analysis', 'heritability'],
    ['Selection Index', '/biometrical-analysis', 'selection_index'],
    ['AMMI', '/biometrical-analysis', 'ammi'],
    ['GGE Biplot', '/biometrical-analysis', 'gge_biplot'],
  ];
  return (
    <div className="rounded-xl p-4 mt-5"
      style={{ background: '#FAFBF9', border: '1px solid #E5E5E0' }}
      data-testid={`${prefix}-handoff`}>
      <div className="text-xs uppercase tracking-wide mb-2" style={{ color: '#6F695F' }}>
        Next step — hand off to BKAgriD statistics
      </div>
      <div className="flex flex-wrap gap-2">
        {targets.map(([label, path, key]) => (
          <button key={key} onClick={() => onHandoff(path, key)}
            data-testid={`${prefix}-handoff-${label.toLowerCase().replace(/\s+/g,'-')}`}
            className="px-3 py-1.5 rounded-md text-xs"
            style={{ background: '#fff', border: '1px solid #2D6A4F', color: '#1B4332' }}>
            {label} →
          </button>
        ))}
      </div>
    </div>
  );
}

export function ExportBtn({ onClick, children, prefix }) {
  return (
    <button onClick={onClick} className="px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1"
      style={{ background: '#F3F6F3', border: '1px solid #E5E5E0', color: '#4F4B44' }}
      data-testid={`${prefix || ''}-export-${String(children).toLowerCase()}`}>
      <Download size={11} /> {children}
    </button>
  );
}

/** "View as Heatmap" — reads the File, passes it into /phenoscan?tab=heatmap via router state. */
export function ViewHeatmapBtn({ file, kind, colormap = 'viridis', title,
                                 pixelsPerCm, scalarValue, navigate, testid }) {
  const onClick = async () => {
    try {
      const fr = new FileReader();
      fr.onload = () => {
        const dataUrl = String(fr.result || '');
        const comma = dataUrl.indexOf(',');
        const b64 = comma > -1 ? dataUrl.substring(comma + 1) : dataUrl;
        navigate('/phenoscan?tab=heatmap', {
          state: {
            heatmapPreset: {
              kind, colormap, title, filename: file.name, imageBase64: b64,
              pixelsPerCm, scalarValue,
            },
          },
        });
      };
      fr.readAsDataURL(file);
    } catch (e) { console.error(e); }
  };
  return (
    <button onClick={onClick}
      data-testid={testid || 'view-as-heatmap-btn'}
      className="px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1"
      style={{ background: '#FFF7ED', border: '1px solid #FDBA74', color: '#9A3412', fontWeight: 600 }}>
      <Flame size={11} /> View as Heatmap →
    </button>
  );
}

export function StatCard({ title, rows }) {
  return (
    <div className="rounded-xl p-3" style={{ border: '1px solid #E5E5E0', background: '#FFFFFF' }}>
      <div className="text-xs uppercase tracking-wide mb-2" style={{ color: '#4F4B44' }}>{title}</div>
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} style={{ borderBottom: '1px dashed #F0EFEA' }}>
              <td style={{ color: '#6F695F', padding: '4px 6px 4px 0' }}>{k}</td>
              <td style={{ color: '#1B4332', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                {v == null ? '—' : String(v)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
